import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  matchesConfirmationText,
  normalizeConfirmationText,
} from "../../../../lib/confirmationText";
import { FieldGroup } from "../../../../models/audit/library";

interface DeleteLibraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldGroup: FieldGroup;
  onConfirm: () => Promise<void>;
  loading?: boolean;
}

export function DeleteLibraryModal({
  open,
  onOpenChange,
  fieldGroup,
  onConfirm,
  loading,
}: DeleteLibraryModalProps) {
  const [confirmText, setConfirmText] = useState("");

  const isConfirmValid = matchesConfirmationText(confirmText, fieldGroup.name);

  const handleConfirm = async () => {
    if (!isConfirmValid) return;
    await onConfirm();
    setConfirmText("");
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setConfirmText("");
    }
    onOpenChange(open);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <AlertDialogTitle>Delete Library</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2 text-stone-600">
            This will permanently delete the{" "}
            <span className="font-semibold text-stone-900">
              {fieldGroup.name}
            </span>{" "}
            library and all of its items. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4 space-y-3">
          <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-800">
            <p className="font-medium">Warning:</p>
            <ul className="mt-1 list-disc list-inside text-red-700 space-y-0.5">
              <li>All items in this library will be deleted</li>
              <li>All control relationships will be removed</li>
              <li>This cannot be undone</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-name" className="text-stone-700">
              Type{" "}
              <span className="font-semibold text-stone-900">
                {normalizeConfirmationText(fieldGroup.name)}
              </span>{" "}
              to confirm
            </Label>
            <Input
              id="confirm-name"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={normalizeConfirmationText(fieldGroup.name)}
              className="font-mono"
              autoComplete="off"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmValid || loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? "Deleting..." : "Delete Library"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
